import { Box, Drawer, Stack, TextField, Typography } from "@mui/material";
import { CancelOutlined } from "@mui/icons-material";
import { ApprovedUserStatement, formatStatementCategory } from "../approvedUsersUtils";

type Props = {
    selectedUser: Record<string, any>;
    isDrawerOpen: boolean;
    handleCloseDrawer: (value: boolean) => void;
}

const fieldTitles: Record<string, string> = {
    firstName: "First name",
    lastName: "Last Name",
    ducExpiresOn: "DUC Expiration Date",
    ducCategory: "DUC Category",
    ducGroupType: "DUC Type",
    role: "User Role",
    institution: "Institution",
};

const roleLabels: Record<string, string> = {
    LEAD: "Lead Investigator",
    SUB_APPLICANT: "Sub-Applicant",
};

// Per-field display formatting, keyed by fieldOrder entry; fields with no entry render rawValue as-is.
const fieldValueFormatters: Partial<Record<string, (rawValue: any) => any>> = {
    role: (rawValue) => roleLabels[rawValue] ?? rawValue,
    ducCategory: formatStatementCategory,
};

const fieldOrder = ["firstName", "lastName", "ducExpiresOn", "ducCategory", "ducGroupType", "role", "institution"];

const statementFieldSx = {
    '& .MuiInputBase-root textarea': {
        maxHeight: '50vh',
        // MUI's TextareaAutosize sets an inline `overflow: hidden` style on the
        // textarea whenever `maxRows` isn't provided, which otherwise overrides
        // this rule and hides the scrollbar even though maxHeight still clips the text.
        overflowY: 'auto !important',
    },
};

const ApprovedUserDetails = (props: Props) => {
    const { selectedUser, isDrawerOpen, handleCloseDrawer } = props;

    const handleClose = () => {
        handleCloseDrawer(false);
    };

    const statements: ApprovedUserStatement[] = selectedUser?.researchUseStatements ?? [];
    const hasMultipleStatements = statements.length > 1;

    return (
        <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={handleClose}
            sx={{ width: 600 }}
        >
            <Box sx={{ paddingX: '32px', paddingY: '48px', width: 600 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                }}>
                    <Typography variant="h5">
                        {selectedUser?.firstName} {selectedUser?.lastName}
                    </Typography>
                    <CancelOutlined sx={{ cursor: 'pointer' }} onClick={handleClose}/>
                </Box>
                <Stack gap={3}>
                    {fieldOrder.map((key) => {
                        const rawValue = selectedUser?.[key] ?? '';
                        const value = fieldValueFormatters[key]?.(rawValue) ?? rawValue;
                        return (
                            <TextField
                                key={key}
                                label={fieldTitles[key]}
                                value={value}
                                variant="outlined"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        );
                    })}
                    {statements.map(({ category, statement }, index) => (
                        <TextField
                            key={`statement-${index}`}
                            label={hasMultipleStatements ? `Research Use Statement — ${formatStatementCategory(category)}` : "Research Use Statement"}
                            value={statement ?? ''}
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={8}
                            InputProps={{ readOnly: true }}
                            sx={statementFieldSx}
                        />
                    ))}
                </Stack>
            </Box>
        </Drawer>
    );
}

export default ApprovedUserDetails;
